<template>
  <div class="organization-invoices mt-5" v-if="organization">

    <v-row justify="end">
      <v-col cols="3">
        <v-autocomplete
            :placeholder="$t('proforma-invoice.search-by-number')"
            v-model="selectedInvoiceToSearch"
            :search-input.sync="searchByInvoiceNumberQuery"
            return-object
            no-filter
            clearable
            :items="searchInvoiceByNumberResult"
        >
          <template #selection="{ item }">
            {{ $t('short-number') }} {{ item.invoiceReference }}
          </template>
          <template #item="{ item }">
            {{ $t('short-number') }} {{ item.invoiceReference }}
          </template>
        </v-autocomplete>
      </v-col>
    </v-row>

    <v-card>
      <v-card-text>
        <v-row>
          <v-col>
            <date-picker
                label="Date de création"
                v-model="rangeDate"
                clearable
                range
            />
          </v-col>
          <v-col>
            <date-picker
                label="Date de la période"
                v-model="rangePeriodDate"
                clearable
                range
            />
          </v-col>
          <v-col>
            <v-select
                :label="$t('invoice.document-kind')"
                v-model="filters.documentKind"
                :items="documentKindOptions"
                hide-details
                clearable
            />
          </v-col>
          <v-col>
            <v-select
                :label="$t('invoice.content-type')"
                clearable
                v-model="filters.contentType"
                :items="contentTypeOptions"
            />
          </v-col>
          <v-col cols="auto">
            <v-select
                :label="$t('invoice.payment-status')"
                v-model="filters.paymentStatus"
                :items="paymentStatusOptions"
                clearable
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-divider class="my-6" />

    <v-data-table
        :headers="headers"
        :items="invoices"
        :options.sync="options"
        :loading="loading"
        :server-items-length="total"
    >
      <template #item.createdAt="{item}">
        <span class="font-weight-light">{{ item.createdAt | formatDate($t('format_date')) }}</span>
      </template>

      <template #item.documentKind="{item}">
        <span class="font-weight-medium">{{ $t('invoice.document-kinds.' + item.documentKind) }}</span>
        <div class="font-weight-thin">{{ $t('enums.invoice-content-type.' + item.invoiceContentType) }}</div>
      </template>

      <template #item.startDate="{item}">
        <span class="font-weight-light">{{ formatUtcDate(item.startDate, $t('format_date')) }} - {{ formatUtcDate(item.endDate, $t('format_date')) }}</span>
      </template>

      <template #item.netTotal="{item}">
        <span style="white-space: nowrap">{{ item.netTotal ? numberFormat(item.netTotal) + ' €' : '-' }}</span>
      </template>

      <template #item.finalTotal="{item}">
        <span style="white-space: nowrap">{{ item.finalTotal ? numberFormat(item.finalTotal) + ' €' : '-' }}</span>
      </template>

      <template #item.status="{item}">
        {{ $t('invoice.statues.' + item.status) }}
      </template>

      <template #item.paymentStatus="{item}">
        <div class="text-center">
          <v-tooltip bottom v-if="item.paymentStatus === 'paid'">
            <template #activator="{ on, attrs }">
              <v-icon color="green" v-bind="attrs" v-on="on">mdi-cash-check</v-icon>
            </template>
            <span>{{ $t('invoice.payment-statuses.paid') }}</span>
          </v-tooltip>
          <v-tooltip bottom v-else-if="item.paymentStatus === 'pending'">
            <template #activator="{ on, attrs }">
              <v-icon color="orange" v-bind="attrs" v-on="on">mdi-cash-clock</v-icon>
            </template>
            <span>{{ $t('invoice.payment-statuses.pending') }}</span>
          </v-tooltip>
          <v-tooltip bottom v-else-if="item.paymentStatus === 'failed'">
            <template #activator="{ on, attrs }">
              <v-icon color="red" v-bind="attrs" v-on="on">mdi-cash-remove</v-icon>
            </template>
            <span>{{ $t('invoice.payment-statuses.failed') }}</span>
          </v-tooltip>
          <span v-else class="text--disabled">-</span>
        </div>
      </template>

      <template #item.emailDelivered="{value}">
        <div class="text-center">
          <v-icon color="green" v-if="value">mdi-email-fast-outline</v-icon>
          <v-icon color="red" small v-else>mdi-close</v-icon>
        </div>
      </template>

      <template #item.options="{item}">
        <v-btn icon small @click="openInvoice(item.id)">
          <v-icon>mdi-eye-outline</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-row class="mt-3">
      <v-col>
        <v-btn small @click="downloadCsv">
          <v-icon left>mdi-download</v-icon>
          {{ $t('export') }}
        </v-btn>
      </v-col>
    </v-row>

    <invoice-show-modal ref="invoiceModal" @updated="loadData" />

  </div>
</template>

<script>
import InvoiceRepository from "@repository/InvoiceRepository";
import InvoiceShowModal from "@pages/Invoice/modals/InvoiceShowModal.vue";

export default {
  components: { InvoiceShowModal },

  data() {
    return {
      loading: false,
      rangeDate: [],
      rangePeriodDate: [],
      filters: {
        documentKind: null,
        contentType: null,
        paymentStatus: null,
      },
      selectedInvoiceToSearch: null,
      searchByInvoiceNumberQuery: null,
      searchInvoiceByNumberResult: [],
      total: 0,
      invoices: [],
      options: null,
    }
  },

  watch: {
    filters: {
      handler() {
        if (this.options?.page > 1) {
          this.options.page = 1
        } else {
          this.loadData()
        }
      },
      deep: true,
    },

    rangeDate() { this.loadData() },
    rangePeriodDate() { this.loadData() },
    options() { this.loadData() },

    selectedInvoiceToSearch(val) {
      if (val) {
        this.openInvoice(val.id)
        this.selectedInvoiceToSearch = null
        this.searchByInvoiceNumberQuery = null
        this.searchInvoiceByNumberResult = []
      }
    },

    searchByInvoiceNumberQuery(query) {
      if (query) {
        InvoiceRepository.searchByNumber(query).then(result => {
          this.searchInvoiceByNumberResult = result
        }).catch(() => {})
      }
    },
  },

  computed: {
    organization() {
      return this.$parent.organization ?? null
    },

    headers() {
      return [
        { text: this.$t('created-at'), value: 'createdAt' },
        { text: this.$t('invoice.document-kind'), value: 'documentKind' },
        { text: this.$t('invoice.reference'), value: 'invoiceReference' },
        { text: this.$t('period'), value: 'startDate' },
        { text: this.$t('invoice.recipient'), value: 'recipientName' },
        { text: this.$t('invoice.total-ht'), value: 'netTotal' },
        { text: this.$t('invoice.total'), value: 'finalTotal' },
        { text: this.$t('status'), value: 'status' },
        { text: this.$t('invoice.payment'), value: 'paymentStatus', sortable: false, width: 90 },
        { text: '', value: 'emailDelivered', sortable: false, width: 50 },
        { value: 'options', width: 50 },
      ]
    },

    documentKindOptions() {
      return [
        { text: this.$t('invoice.document-kinds.invoice'), value: 'invoice' },
        { text: this.$t('invoice.document-kinds.credit-note'), value: 'credit-note' },
      ]
    },

    contentTypeOptions() {
      return [
        { text: this.$tc('enums.invoice-content-type.subscription', 2), value: 'subscription' },
        { text: this.$t('enums.invoice-content-type.usage'), value: 'usage' },
      ]
    },

    paymentStatusOptions() {
      return [
        { text: this.$t('invoice.payment-statuses.paid'), value: 'paid' },
        { text: this.$t('invoice.payment-statuses.pending'), value: 'pending' },
        { text: this.$t('invoice.payment-statuses.failed'), value: 'failed' },
      ]
    },
  },

  methods: {
    loadData() {
      if (!this.organization || !this.options) return

      this.loading = true
      const data = {
        recipientType: 'organization',
        organizationId: this.organization.id,
        ...this.filters,
      }

      if (this.rangeDate?.length > 0) {
        data.startDate = this.rangeDate[0]
        data.endDate = this.rangeDate[1]
      }
      if (this.rangePeriodDate?.length > 0) {
        data.startPeriodDate = this.rangePeriodDate[0]
        data.endPeriodDate = this.rangePeriodDate[1]
      }

      if (this.options.itemsPerPage > -1) {
        data.offset = (this.options.page * this.options.itemsPerPage) - this.options.itemsPerPage
      }
      data.limit = this.options.itemsPerPage

      if (this.options.sortBy?.length > 0) {
        data.sortBy = this.options.sortBy[0]
      }
      if (this.options.sortDesc?.length > 0) {
        data.sortOrder = this.options.sortDesc[0] ? 'desc' : 'asc'
      }

      InvoiceRepository.search(data).then(response => {
        this.total = response.total
        this.invoices = response.items
        this.loading = false
      }).catch(error => {
        this.notifyError(error)
        this.loading = false
      })
    },

    openInvoice(id) {
      this.$refs.invoiceModal.open(id)
    },

    downloadCsv() {
      const data = {
        recipientType: 'organization',
        organizationId: this.organization.id,
        ...this.filters,
      }
      if (this.rangeDate?.length > 0) {
        data.startDate = this.rangeDate[0]
        data.endDate = this.rangeDate[1]
      }
      if (this.rangePeriodDate?.length > 0) {
        data.startPeriodDate = this.rangePeriodDate[0]
        data.endPeriodDate = this.rangePeriodDate[1]
      }

      this.showLoading(true)
      InvoiceRepository.exportCsv(data).then(() => {
        this.showLoading(false)
      }).catch(err => {
        this.notifyError(err)
        this.showLoading(false)
      })
    },
  },
}
</script>
